import React, { useEffect, useState } from "react";

// Replaces react-back-to-top-button 0.0.14 (TO-1849), which bundled react 15, fbjs, isomorphic-fetch and node-fetch.
// Same behaviour and markup as the props App.js gave it: a fixed button with class "back-to-top" that fades in once
// the page is scrolled past `showAt` pixels and scrolls back to the top with the same easing and timing.
// It shows at every screen size, as the old package did (Jim, TO-1849 16333). The keyboard focus ring and the
// reduced-motion fade are in orbit-app-build.scss (an inline style can't target :focus-visible or a media query).

const easings = {
  easeOutSine: (pos) => Math.sin(pos * (Math.PI / 2)),
  easeInOutSine: (pos) => -0.5 * (Math.cos(Math.PI * pos) - 1),
  easeInOutQuint: (pos) => {
    const p = pos / 0.5;
    return p < 1 ? 0.5 * p ** 5 : 0.5 * (p - 2) ** 5 + 2;
  },
};

// The package's timing: 1/60 s per frame, duration distance/speed seconds, clamped to 0.1-0.8 s.
const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const scrollToTop = (speed, easing) => {
  // With reduced motion asked for, jump to the top instead of animating (TO-1849 16335)
  if (prefersReducedMotion()) {
    window.scrollTo(0, 0);
    return;
  }
  const startY = window.scrollY || document.documentElement.scrollTop;
  const ease = easings[easing] || easings.easeOutSine;
  const duration = Math.max(0.1, Math.min(startY / (speed || 2000), 0.8));
  let elapsed = 0;

  const tick = () => {
    elapsed += 1 / 60;
    const progress = elapsed / duration;
    if (progress < 1) {
      window.scrollTo(0, startY - startY * ease(progress));
      window.requestAnimationFrame(tick);
    } else {
      window.scrollTo(0, 0);
    }
  };
  tick();
};

const buttonStyle = (visible) => ({
  fontSize: "45px",
  position: "fixed",
  right: 0,
  bottom: 0,
  margin: "30px",
  borderRadius: "50%",
  border: "none",
  opacity: visible ? 1 : 0,
  visibility: visible ? "visible" : "hidden",
  transition: "0.4s opacity ease",
  cursor: "pointer",
  background: "transparent",
});

const BackToTop = ({ showAt = 1000, speed, easing, children }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible((window.pageYOffset || document.documentElement.scrollTop) > showAt);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [showAt]);

  return (
    <button
      type="button"
      className="back-to-top"
      aria-label="Back to top"
      style={buttonStyle(visible)}
      onClick={() => scrollToTop(speed, easing)}
    >
      {children || "UP"}
    </button>
  );
};

export default BackToTop;
